import { MessageCircle, Share2, Send, ChevronDown, ChevronUp, Heart, MoreHorizontal, Pencil, Trash2, X, Check as CheckIcon, Flag, EyeOff, Ban, Rocket } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { YouTubeEmbed, SpotifyCard, SoundCloudEmbed, DeezerCard, AppleMusicCard, TidalCard } from "@/components/embeds";
import { toast } from "sonner";
import { popXP } from "@/components/CreditPop";
import { haptic } from "@/lib/celebrate";

const REACTIONS = [
  { type: "love", emoji: "\u2764\uFE0F", label: "J'adore" },
  { type: "fire", emoji: "\uD83D\uDD25", label: "Feu" },
  { type: "laugh", emoji: "\uD83D\uDE02", label: "Haha" },
  { type: "wow", emoji: "\uD83D\uDE2E", label: "Wow" },
  { type: "sad", emoji: "\uD83D\uDE22", label: "Triste" },
];

const EMOJI_MAP = {
  love: "\u2764\uFE0F",
  fire: "\uD83D\uDD25",
  laugh: "\uD83D\uDE02",
  wow: "\uD83D\uDE2E",
  sad: "\uD83D\uDE22",
};

export const PostCard = ({ post, onLike, onReact, onDelete, onUpdate, onHide, onBlock, compact = false }) => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [userReaction, setUserReaction] = useState(post.user_reaction || null);
  const [reactions, setReactions] = useState(post.reactions || { counts: {}, total: 0 });
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);
  const timerRef = useRef(null);

  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);

  const [showMenu, setShowMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content || "");
  const [editLoading, setEditLoading] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [boostLoading, setBoostLoading] = useState(false);
  const [isBoosted, setIsBoosted] = useState(post.is_boosted || false);
  const menuRef = useRef(null);

  const isOwner = user?.role === "artist" && user?.artist_id === post.artist_id;

  useEffect(() => {
    const handleClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleEdit = async () => {
    if (!editContent.trim() || editLoading) return;
    setEditLoading(true);
    try {
      const res = await api.put(`/posts/${post.id}`, { content: editContent.trim() });
      if (onUpdate) onUpdate(post.id, res.data.post);
      setEditing(false);
    } catch (err) { console.error(err); }
    setEditLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer cette publication ?")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      setDeleted(true);
      if (onDelete) onDelete(post.id);
    } catch (err) { console.error(err); }
  };

  const handleHide = async () => {
    try {
      await api.post(`/posts/${post.id}/hide`);
      setDeleted(true);
      if (onHide) onHide(post.id);
    } catch (err) { console.error(err); }
  };

  const handleBlock = async () => {
    if (!window.confirm(`Bloquer ${post.artist_name} ? Vous ne verrez plus ses publications.`)) return;
    try {
      await api.post(`/reports/block/${post.artist_id}`);
      // Need the user_id of the artist, but we have artist_id. Let's use artist_id lookup
      // Actually the block endpoint expects a user_id. Let's get it from the artist profile
      setDeleted(true);
      if (onBlock) onBlock(post.artist_id);
    } catch (err) { console.error(err); }
  };

  const handleReport = async (reason) => {
    try {
      await api.post("/reports", { target_type: "post", target_id: post.id, reason });
      setShowReportModal(false);
      toast.success("Signalement envoye. Merci !");
    } catch (e) {
      toast.success(e.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/posts/${post.id}/comments/${commentId}`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setCommentsCount((c) => c - 1);
    } catch (err) { console.error(err); }
  };

  const handleLike = async () => {
    if (!user || !onLike) return;
    const prev = liked;
    setLiked(!liked);
    setLikesCount((c) => (liked ? c - 1 : c + 1));
    try {
      await onLike(post.id);
      if (!prev) {
        // Newly liked: pop a small XP gain and haptic feedback
        haptic(40);
        popXP(1);
      }
    } catch {
      setLiked(prev);
      setLikesCount((c) => (prev ? c : c - 1));
    }
  };

  const handleReaction = async (reactionType) => {
    if (!user || !onReact) return;
    setShowPicker(false);
    const prevReaction = userReaction;
    const prevReactions = reactions;
    if (userReaction === reactionType) {
      setUserReaction(null);
    } else {
      setUserReaction(reactionType);
    }
    try {
      const result = await onReact(post.id, reactionType);
      setUserReaction(result.reaction_type);
      setReactions(result.reactions);
      if (result.reaction_type && result.reaction_type !== prevReaction) {
        haptic(30);
      }
    } catch {
      setUserReaction(prevReaction);
      setReactions(prevReactions);
    }
  };

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => setShowPicker(true), 400);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const toggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoaded) {
      try {
        const res = await api.get(`/posts/${post.id}/comments`);
        setComments(res.data.comments);
        setCommentsLoaded(true);
      } catch (err) { console.error(err); }
    }
  };

  const submitComment = async () => {
    if (!commentText.trim() || commentLoading) return;
    setCommentLoading(true);
    try {
      const res = await api.post(`/posts/${post.id}/comments`, { content: commentText.trim() });
      setComments((prev) => [res.data.comment, ...prev]);
      setCommentsCount((c) => c + 1);
      setCommentText("");
      // Refresh user credits immediately after commenting
      refreshUser();
    } catch (err) { console.error(err); }
    setCommentLoading(false);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/feed`;
    const text = `${post.artist_name} sur ReallyFan: ${(post.content || "").slice(0, 100)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "ReallyFan", text, url });
      } catch (err) { console.error(err); }
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Lien copie !");
    }
  };

  const reactionsTotal = reactions?.total || 0;
  const displayEmoji = userReaction ? EMOJI_MAP[userReaction] : null;

  const renderEmbed = () => {
    if (!post.embed_url) return null;
    const url = post.embed_url;
    const meta = post.embed_meta;
    if (url.includes("youtube.com") || url.includes("youtu.be")) return <YouTubeEmbed url={url} postId={post.id} />;
    if (url.includes("spotify.com")) return <SpotifyCard url={url} meta={meta} postId={post.id} />;
    if (url.includes("soundcloud.com") || url.includes("on.soundcloud.com")) return <SoundCloudEmbed url={url} />;
    if (url.includes("deezer.com") || url.includes("deezer.page.link")) return <DeezerCard url={url} meta={meta} postId={post.id} />;
    if (url.includes("music.apple.com")) return <AppleMusicCard url={url} meta={meta} postId={post.id} />;
    if (url.includes("tidal.com")) return <TidalCard url={url} meta={meta} postId={post.id} />;
    // No external link fallback - keep users in app
    return (
      <div className="mt-3 flex items-center gap-3 bg-foreground/5 border border-foreground/10 rounded-2xl p-3 w-full max-w-full">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-muted-foreground truncate">{url}</p>
        </div>
      </div>
    );
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}j`;
  };

  const handleBoost = async (credits) => {
    setBoostLoading(true);
    try {
      await api.post(`/posts/${post.id}/boost`, { credits });
      setIsBoosted(true);
      setShowBoostModal(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de boost");
    }
    setBoostLoading(false);
  };

  const BOOST_OPTIONS = [
    { credits: 10, hours: "6h", color: "from-blue-400 to-blue-500" },
    { credits: 25, hours: "24h", color: "from-[#FF6B35] to-[#FF8FA3]" },
    { credits: 50, hours: "3j", color: "from-purple-500 to-pink-500" },
    { credits: 100, hours: "7j", color: "from-yellow-400 to-orange-500" },
  ];

  const topReactions = Object.entries(reactions?.counts || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  if (deleted) return null;

  return (
    <div className={`bg-card border rounded-3xl p-5 transition-colors duration-300 shadow-sm ${isBoosted ? "border-[#FF6B35]/50 ring-1 ring-[#FF6B35]/20" : "border-border hover:border-[#FF6B35]/30"}`} data-testid={`post-card-${post.id}`}>
      {/* Boost badge */}
      {isBoosted && (
        <div className="flex items-center gap-1.5 mb-2" data-testid={`boost-badge-${post.id}`}>
          <Rocket className="w-3.5 h-3.5 text-[#FF6B35]" />
          <span className="text-[10px] font-bold text-[#FF6B35] uppercase tracking-wider">Mis en avant</span>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center cursor-pointer" onClick={() => navigate(`/artist/${post.artist_id}`)}>
          {post.artist_avatar ? (
            <img src={post.artist_avatar} alt="" className="w-full h-full rounded-full object-cover" />
          ) : (
            <span className="text-white font-heading font-bold text-sm">{(post.artist_name || "?")[0].toUpperCase()}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-heading font-semibold text-sm text-card-foreground cursor-pointer hover:text-[#FF6B35] transition-colors truncate" onClick={() => navigate(`/artist/${post.artist_id}`)}>
              {post.artist_name}
            </p>
            {post.artist_verified && (
              <svg className="w-4 h-4 text-[#7000FF] shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{timeAgo(post.created_at)}</p>
        </div>
        {user && (
          <div className="relative" ref={menuRef}>
            <button onClick={() => setShowMenu(!showMenu)} className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-border transition-colors" data-testid={`post-menu-btn-${post.id}`}>
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-border rounded-xl shadow-lg overflow-hidden z-30" data-testid={`post-menu-${post.id}`}>
                {isOwner ? (
                  <>
                    <button onClick={() => { setEditing(true); setShowMenu(false); setEditContent(post.content || ""); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-card-foreground/80 hover:bg-primary/10 transition-colors" data-testid={`post-edit-btn-${post.id}`}>
                      <Pencil className="w-3.5 h-3.5" /> Modifier
                    </button>
                    <button onClick={() => { handleDelete(); setShowMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-red-500 hover:bg-red-50 transition-colors" data-testid={`post-delete-btn-${post.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { handleHide(); setShowMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-card-foreground/80 hover:bg-primary/10 transition-colors" data-testid={`post-hide-btn-${post.id}`}>
                      <EyeOff className="w-3.5 h-3.5" /> Masquer ce post
                    </button>
                    <button onClick={() => { setShowReportModal(true); setShowMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-card-foreground/80 hover:bg-primary/10 transition-colors" data-testid={`post-report-btn-${post.id}`}>
                      <Flag className="w-3.5 h-3.5" /> Signaler
                    </button>
                    <button onClick={() => { handleBlock(); setShowMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-red-500 hover:bg-red-50 transition-colors" data-testid={`post-block-btn-${post.id}`}>
                      <Ban className="w-3.5 h-3.5" /> Bloquer {post.artist_name}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {editing ? (
        <div className="mb-3" data-testid={`post-edit-form-${post.id}`}>
          <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} className="w-full bg-white border border-border rounded-xl px-3 py-2 text-sm text-card-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none resize-none" rows={3} />
          <div className="flex gap-2 mt-2 justify-end">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs text-muted-foreground bg-border rounded-full hover:bg-border transition-colors" data-testid={`post-edit-cancel-${post.id}`}>
              <X className="w-3 h-3 inline mr-1" />Annuler
            </button>
            <button onClick={handleEdit} disabled={editLoading || !editContent.trim()} className="px-3 py-1.5 text-xs text-white bg-[#FF6B35] rounded-full hover:bg-[#E85A25] transition-colors disabled:opacity-50" data-testid={`post-edit-save-${post.id}`}>
              <CheckIcon className="w-3 h-3 inline mr-1" />{editLoading ? "..." : "Enregistrer"}
            </button>
          </div>
        </div>
      ) : (
        post.content && <p className="text-sm text-card-foreground/80 leading-relaxed whitespace-pre-wrap">{post.content}</p>
      )}

      {/* Media */}
      {post.media_urls?.length > 0 && (
        <div className="mt-3 grid gap-2">
          {post.media_urls.map((url) => (<img key={url} src={url} alt="" className="w-full rounded-2xl object-cover max-h-96" />))}
        </div>
      )}

      {/* Embed */}
      {renderEmbed()}

      {/* Reaction summary */}
      {reactionsTotal > 0 && (
        <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground" data-testid={`reactions-summary-${post.id}`}>
          <div className="flex -space-x-0.5">
            {topReactions.map(([type]) => (<span key={type} className="text-sm">{EMOJI_MAP[type]}</span>))}
          </div>
          <span className="font-mono-credits">{reactionsTotal}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <div className="relative" ref={pickerRef}>
          <button
            onClick={() => { if (onReact) { handleReaction(userReaction || "love"); } else { handleLike(); } }}
            onMouseEnter={onReact ? handleMouseEnter : undefined}
            onMouseLeave={onReact ? handleMouseLeave : undefined}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${(onReact ? userReaction : liked) ? "text-[#FF6B35] bg-[#FF6B35]/8" : "text-muted-foreground hover:text-[#FF8FA3] hover:bg-[#FF8FA3]/8"}`}
            data-testid={`like-btn-${post.id}`}
          >
            {displayEmoji ? <span className="text-sm leading-none">{displayEmoji}</span> : <Heart className="w-4 h-4" fill={(onReact ? userReaction : liked) ? "currentColor" : "none"} />}
            <span className="font-mono-credits">{onReact ? reactionsTotal : likesCount}</span>
          </button>
          {showPicker && (
            <div className="absolute bottom-full left-0 mb-2 flex gap-1 bg-card border border-border rounded-full px-2 py-1.5 shadow-xl z-50" data-testid={`reaction-picker-${post.id}`} onMouseEnter={() => { if (timerRef.current) clearTimeout(timerRef.current); setShowPicker(true); }} onMouseLeave={() => setShowPicker(false)}>
              {REACTIONS.map((r) => (
                <button key={r.type} onClick={() => handleReaction(r.type)} className={`text-xl hover:scale-125 transition-transform duration-150 px-0.5 ${userReaction === r.type ? "scale-125" : ""}`} title={r.label} data-testid={`reaction-${r.type}-${post.id}`}>{r.emoji}</button>
              ))}
            </div>
          )}
        </div>

        <button onClick={toggleComments} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${showComments ? "text-[#7000FF] bg-[#7000FF]/8" : "text-muted-foreground hover:text-[#7000FF] hover:bg-[#7000FF]/8"}`} data-testid={`comment-btn-${post.id}`}>
          <MessageCircle className="w-4 h-4" />
          <span>Commenter</span>
          {commentsCount > 0 && <span className="font-mono-credits">({commentsCount})</span>}
        </button>

        <button onClick={handleShare} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-muted-foreground hover:text-[#FF8FA3] hover:bg-[#FF8FA3]/8 transition-all duration-200" data-testid={`share-btn-${post.id}`}>
          <Share2 className="w-4 h-4" />
          <span>Partager</span>
        </button>

        {user && !isBoosted && (
          <button
            onClick={() => setShowBoostModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-muted-foreground hover:text-[#FF6B35] hover:bg-[#FF6B35]/8 transition-all duration-200"
            data-testid={`boost-btn-${post.id}`}
          >
            <Rocket className="w-4 h-4" />
            <span className="hidden sm:inline">Booster</span>
          </button>
        )}
      </div>

      {/* Comments section */}
      {showComments && (
        <div className="mt-3 pt-3 border-t border-border space-y-3" data-testid={`comments-section-${post.id}`}>
          {/* Comment input */}
          {user && (
            <div className="flex gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-white text-[10px] font-bold">{(user.pseudo || "?")[0].toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 flex gap-1.5">
                <input
                  placeholder="Ecrire un commentaire..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitComment()}
                  className="flex-1 bg-white border border-border rounded-full px-3 py-1.5 text-xs text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none"
                  data-testid={`comment-input-${post.id}`}
                />
                <button
                  onClick={submitComment}
                  disabled={!commentText.trim() || commentLoading}
                  className="w-7 h-7 rounded-full bg-[#FF6B35] flex items-center justify-center text-white disabled:opacity-40 hover:scale-110 active:scale-95 transition-transform shrink-0"
                  data-testid={`comment-submit-${post.id}`}
                >
                  <Send className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* Comments list */}
          {comments.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-2">Aucun commentaire</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {comments.map((c) => (
                <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#7000FF] to-[#FF8FA3] flex items-center justify-center shrink-0">
                    {c.user_avatar ? (
                      <img src={c.user_avatar} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-white text-[8px] font-bold">{(c.user_pseudo || "?")[0].toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <button onClick={() => navigate(`/user/${c.user_id}`)} className="font-bold text-[10px] text-card-foreground hover:text-[#FF6B35] transition-colors">{c.user_pseudo}</button>
                      <span className="text-[9px] text-muted-foreground">{timeAgo(c.created_at)}</span>
                      {(isOwner || c.user_id === user?.id) && (
                        <button onClick={() => handleDeleteComment(c.id)} className="text-[9px] text-red-400 hover:text-red-600 transition-colors ml-auto" data-testid={`delete-comment-${c.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-card-foreground/80 leading-relaxed">{c.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowReportModal(false)} data-testid="report-modal">
          <div className="bg-card rounded-2xl p-5 w-80 max-w-[90vw] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-sm text-card-foreground">Signaler cette publication</h3>
              <button onClick={() => setShowReportModal(false)} className="text-muted-foreground hover:text-card-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-1.5">
              {[
                { key: "spam", label: "Spam / Publicite" },
                { key: "inappropriate", label: "Contenu inapproprie" },
                { key: "harassment", label: "Harcelement" },
                { key: "hate", label: "Discours haineux" },
                { key: "other", label: "Autre" },
              ].map((r) => (
                <button key={r.key} onClick={() => handleReport(r.key)} className="w-full text-left px-3 py-2 text-xs text-card-foreground/80 hover:bg-[#FF6B35]/10 hover:text-[#FF6B35] rounded-lg transition-colors" data-testid={`report-reason-${r.key}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Boost Modal */}
      {showBoostModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowBoostModal(false)} data-testid="boost-modal">
          <div className="bg-card rounded-3xl p-5 w-80 max-w-[90vw] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                  <Rocket className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <h3 className="font-heading font-bold text-sm text-card-foreground">Booster cette publication</h3>
              </div>
              <button onClick={() => setShowBoostModal(false)} className="text-muted-foreground hover:text-card-foreground"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">La publication apparaitra en priorite dans le feed et la page Decouvrir.</p>
            <div className="space-y-2">
              {BOOST_OPTIONS.map((opt) => (
                <button
                  key={opt.credits}
                  onClick={() => handleBoost(opt.credits)}
                  disabled={boostLoading}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border hover:border-[#FF6B35]/40 hover:scale-[1.01] active:scale-[0.99] transition-all group"
                  data-testid={`boost-opt-${opt.credits}`}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${opt.color} flex items-center justify-center shrink-0`}>
                    <Rocket className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-bold text-sm text-card-foreground group-hover:text-[#FF6B35] transition-colors">{opt.hours} de mise en avant</p>
                    <p className="text-[10px] text-muted-foreground">Priorite dans le feed + badge</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono-credits font-bold text-sm text-[#FF6B35]">{opt.credits}</p>
                    <p className="text-[9px] text-muted-foreground">credits</p>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-center text-muted-foreground mt-3">Ton solde : <span className="font-mono-credits font-bold text-[#FF6B35]">{user?.credits_balance || 0}</span> credits</p>
          </div>
        </div>
      )}
    </div>
  );
};
